import { Link } from 'react-router-dom';
import { Instagram, Facebook, Linkedin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { TricolorStripe, TricolorStripeMini } from './TricolorStripe';

export const Footer = () => {
  const { t } = useLanguage();

  const quickLinks = [
    { path: '/', label: t.nav.home },
    { path: '/catalogue', label: t.nav.catalogue },
    { path: '/custom-uniforms', label: t.nav.customUniforms },
    { path: '/about', label: t.nav.about },
    { path: '/contact', label: t.nav.contact },
  ];

  const sportsLinks = [
    { path: '/catalogue?sport=soccer', label: t.categories.soccer.name },
    { path: '/catalogue?sport=basketball', label: t.categories.basketball.name },
    { path: '/catalogue?sport=referee', label: t.categories.referee.name },
    { path: '/catalogue?sport=training', label: t.categories.training.name },
    { path: '/catalogue?sport=accessories', label: t.categories.accessories.name },
  ];

  return (
    <footer className="bg-[#0D0D0D]" data-testid="footer">
      <TricolorStripe />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="space-y-6">
            <Link to="/" className="flex flex-col" data-testid="footer-logo">
              <span className="font-bebas text-[28px] leading-none text-white tracking-tight">
                DM SPORTS
              </span>
              <span className="font-dm text-[10px] tracking-[4px] text-[#CE2B37] uppercase">
                MASS CANADA
              </span>
              <TricolorStripeMini width="40px" className="mt-1" />
            </Link>
            <p className="font-dm text-sm text-white/60 leading-relaxed max-w-xs">
              {t.footer.tagline}
            </p>
            <div className="flex items-center gap-4">
              <a
                href="#"
                className="text-white/60 hover:text-[#CE2B37] transition-colors duration-200"
                data-testid="social-instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="#"
                className="text-white/60 hover:text-[#CE2B37] transition-colors duration-200"
                data-testid="social-facebook"
              >
                <Facebook size={20} />
              </a>
              <a
                href="#"
                className="text-white/60 hover:text-[#CE2B37] transition-colors duration-200"
                data-testid="social-linkedin"
              >
                <Linkedin size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bebas text-xl text-white tracking-wide mb-6">
              {t.footer.quickLinks}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="font-dm text-sm text-white/60 hover:text-white transition-colors duration-200"
                    data-testid={`footer-link-${link.path.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Sports */}
          <div>
            <h3 className="font-bebas text-xl text-white tracking-wide mb-6">
              {t.footer.sports}
            </h3>
            <ul className="space-y-3">
              {sportsLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="font-dm text-sm text-white/60 hover:text-white transition-colors duration-200"
                    data-testid={`footer-sport-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-bebas text-xl text-white tracking-wide mb-6">
              {t.footer.contactUs}
            </h3>
            <div className="space-y-3 font-dm text-sm text-white/60">
              {t.footer.address.map((line, index) => (
                <p key={line} data-testid={`footer-address-line-${index}`}>{line}</p>
              ))}
              <p className="pt-2">{t.footer.email}</p>
              <p>{t.footer.phone}</p>
              <p className="pt-2">{t.footer.hours}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <p className="font-dm text-xs text-white/40 text-center">
            {t.footer.copyright}
          </p>
        </div>
      </div>
    </footer>
  );
};
